import { useState, type ReactNode } from "react";

import { LogoIcon } from "@/components/LogoIcon";
import { Heading, TextLink } from "@/components/typography";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { formatMoney } from "../shared/currency";
import { asPlatforms } from "../shared/platforms";
import type { Campaign } from "../shared/types";
import { eligibilityConstraintLabels } from "../utils/pitching/eligibility";

function campaignBudgetLabel(campaign: Campaign, currency: string) {
    const minLabel = formatMoney(campaign.minPricePerCreator, currency);
    const maxLabel = formatMoney(campaign.maxPricePerCreator, currency);
    if (minLabel && maxLabel) {
        return `— ${minLabel} - ${maxLabel}`;
    }
    if (minLabel) {
        return `— from ${minLabel}`;
    }
    if (maxLabel) {
        return `— up to ${maxLabel}`;
    }
    return null;
}

type BriefDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    campaign: Campaign;
};

/** Full brief, deliverables, sound URL, and brief file. */
function BriefDialog({ open, onOpenChange, campaign }: BriefDialogProps) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle render={<Heading type="h3" tag="h2" />}>
                        {campaign.campaignName || "Brief"}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    {campaign.details ? (
                        <p className="whitespace-pre-wrap text-base/6">{campaign.details}</p>
                    ) : null}
                    {campaign.deliverables ? (
                        <p className="text-sm text-muted-foreground">{campaign.deliverables}</p>
                    ) : null}
                    {campaign.soundUrl ? (
                        <div>
                            <TextLink href={campaign.soundUrl} target="_blank" rel="noreferrer">
                                {campaign.soundUrl}
                            </TextLink>
                        </div>
                    ) : null}
                    {campaign.fileUrl ? (
                        <div>
                            <TextLink href={campaign.fileUrl} target="_blank" rel="noreferrer">
                                Brief File
                            </TextLink>
                        </div>
                    ) : null}
                </div>
            </DialogContent>
        </Dialog>
    );
}

type CampaignSummaryProps = {
    campaign: Campaign;
    company?: string;
    companySector?: string;
    statusAndSettings?: ReactNode;
};

/** Client, sector, platforms, name, budget, eligibility, and the brief. */
export function CampaignSummary({ campaign, company, companySector, statusAndSettings }: CampaignSummaryProps) {
    const [briefOpen, setBriefOpen] = useState(false);
    const platforms = asPlatforms(campaign.campaignPlatform);
    const budgetLabel = campaignBudgetLabel(campaign, campaign.currency || "USD");
    const eligibilityPills = eligibilityConstraintLabels(campaign.eligibility);
    const hasBriefBody = Boolean(campaign.details?.trim());
    const showViewBrief = hasBriefBody || Boolean(campaign.soundUrl || campaign.fileUrl);

    return (
        <>
            {/* Client and platforms sit opposite status and Settings when that slot is passed. */}
            <div className="mb-2 flex items-center justify-between gap-4">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                    {company ? (
                        <span className="text-base/6 uppercase text-muted-foreground">
                            {company}
                        </span>
                    ) : null}
                    {companySector ? (
                        <Badge variant="purple">{companySector}</Badge>
                    ) : null}
                    {platforms.map((platform) => (
                        <LogoIcon key={platform} brand={platform} className="size-5" title={platform} />
                    ))}
                </div>
                {statusAndSettings}
            </div>

            {/* Title and budget sit on one baseline row. */}
            <div className="mb-2 flex flex-wrap items-baseline gap-2">
                <Heading type="h2" className="font-bold leading-none">
                    {campaign.campaignName}
                </Heading>
                {budgetLabel ? (
                    <span className="text-base/6 text-muted-foreground">{budgetLabel}</span>
                ) : null}
            </div>

            {campaign.deliverables ? (
                <p className="mb-2 text-base/6 text-muted-foreground">{campaign.deliverables}</p>
            ) : null}

            {eligibilityPills.length > 0 ? (
                // Eligibility chips wrap in a row.
                <div className="mb-6 flex flex-wrap gap-2">
                    {eligibilityPills.map((pill) => (
                        <Badge key={pill} variant="secondary">
                            {pill}
                        </Badge>
                    ))}
                </div>
            ) : null}

            {showViewBrief ? (
                <div className="mb-12 max-w-3xl">
                    {hasBriefBody ? (
                        <p className="line-clamp-3 text-base/6">{campaign.details}</p>
                    ) : null}
                    <Button
                        type="button"
                        variant="link"
                        className={hasBriefBody ? "mt-2 h-auto p-0" : "h-auto p-0"}
                        onClick={() => setBriefOpen(true)}
                    >
                        View Full Brief
                    </Button>
                </div>
            ) : null}
            <BriefDialog open={briefOpen} onOpenChange={setBriefOpen} campaign={campaign} />
        </>
    );
}

/** Brand, name, platform, budget, and pill rhythm for a campaign-doc wait. */
export function CampaignSummarySkeleton() {
    return (
        <>
            <div className="mb-2 flex items-center justify-between gap-4">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <Skeleton className="h-6 w-32" />
                    <Skeleton className="h-5 w-16" />
                    <Skeleton className="size-5" />
                    <Skeleton className="size-5" />
                </div>
            </div>
            <div className="mb-2 flex flex-wrap items-baseline gap-2">
                <Skeleton className="h-8 w-64" />
                <Skeleton className="h-6 w-36" />
            </div>
            <div className="mb-6 flex flex-wrap gap-2">
                <Skeleton className="h-5 w-20" />
                <Skeleton className="h-5 w-24" />
                <Skeleton className="h-5 w-16" />
            </div>
        </>
    );
}
